import { lazy, Suspense, useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AppShell } from './AppShell';
import { LoginScreen } from '../features/platform/login/LoginScreen';
import { useAuth } from '../shared/auth/AuthContext';

// Platform
const Dashboard = lazy(() => import('../features/platform/dashboard/Dashboard'));
const Invites   = lazy(() => import('../features/platform/invites/Invites'));
const Users     = lazy(() => import('../features/platform/users/Users'));
const Tenants   = lazy(() => import('../features/platform/tenants/Tenants'));
const Pending   = lazy(() => import('../features/platform/pending/Pending'));
const Audit     = lazy(() => import('../features/platform/audit/Audit'));

// Apps
const AppRegistry  = lazy(() => import('../features/apps/AppRegistry'));
const FeatureFlags = lazy(() => import('../features/apps/FeatureFlags'));

// Analytics
const AnalyticsDashboard = lazy(() => import('../features/analytics/AnalyticsDashboard'));
const Reports            = lazy(() => import('../features/analytics/Reports'));
const GrafanaEmbed       = lazy(() => import('../features/analytics/GrafanaEmbed'));

// WCR
const WcrDashboard = lazy(() => import('../features/wcr/WcrDashboard'));
const WcrRefData    = lazy(() => import('../features/wcr/WcrRefData'));
const WcrVehicles = lazy(() => import('../features/wcr/WcrVehicles'));
const WcrContainers = lazy(() => import('../features/wcr/WcrContainers'));
const WcrBeacons  = lazy(() => import('../features/wcr/WcrBeacons'));
const WcrOrganizations = lazy(() => import('../features/wcr/WcrOrganizations'));
const WcrFacilities    = lazy(() => import('../features/wcr/WcrFacilities'));
const WcrHmaSyncLog    = lazy(() => import('../features/wcr/WcrHmaSyncLog'));
const WcrAuditLog      = lazy(() => import('../features/wcr/WcrAuditLog'));
const WcrVehicleClaims = lazy(() => import('../features/wcr/WcrVehicleClaims'));
const EntityMatch       = lazy(() => import('../features/wcr/EntityMatch'));
const EntityAliasGlossary = lazy(() => import('../features/wcr/EntityAliasGlossary'));
const ResolveJobRuns   = lazy(() => import('../features/wcr/ResolveJobRuns'));
const EfodsaRolesPermissions = lazy(() => import('../features/wcr/EfodsaRolesPermissions'));
const TenantAssets      = lazy(() => import('../features/wcr/TenantAssets'));
const WcrChat           = lazy(() => import('../features/wcr/WcrChat'));

// Hauler
const HaulerTenants    = lazy(() => import('../features/hauler/HaulerTenants'));
const HaulerContracts  = lazy(() => import('../features/hauler/HaulerContracts'));
const HaulerFacilities = lazy(() => import('../features/hauler/HaulerFacilities'));
const HaulerVehicles   = lazy(() => import('../features/hauler/HaulerVehicles'));
const HaulerDriverSessions = lazy(() => import('../features/hauler/HaulerDriverSessions'));
const HaulerMovementGroups = lazy(() => import('../features/hauler/HaulerMovementGroups'));
const ServicedSeds         = lazy(() => import('../features/hauler/ServicedSeds'));

// Rehoreca
const ActivationOrders     = lazy(() => import('../features/rehoreca/ActivationOrders'));
const DeactivationRequests = lazy(() => import('../features/rehoreca/DeactivationRequests'));
const RehorecaTenants      = lazy(() => import('../features/rehoreca/RehorecaTenants'));
const Submissions          = lazy(() => import('../features/rehoreca/Submissions'));
const Organizations        = lazy(() => import('../features/rehoreca/Organizations'));
const BusinessGroups       = lazy(() => import('../features/rehoreca/BusinessGroups'));
const RehorecaFacilities   = lazy(() => import('../features/rehoreca/RehorecaFacilities'));
const GuestNights          = lazy(() => import('../features/rehoreca/GuestNights'));
const PylonMapping         = lazy(() => import('../features/rehoreca/PylonMapping'));
const PylonClients         = lazy(() => import('../features/rehoreca/PylonClients'));
const PylonItems           = lazy(() => import('../features/rehoreca/PylonItems'));
const PylonLog             = lazy(() => import('../features/rehoreca/PylonLog'));
const LabelBatches         = lazy(() => import('../features/rehoreca/LabelBatches'));
const CertificateDiagnostics = lazy(() => import('../features/rehoreca/CertificateDiagnostics'));

// City
const Municipalities = lazy(() => import('../features/city/Municipalities'));
const Producers      = lazy(() => import('../features/city/Producers'));
const WasteBins      = lazy(() => import('../features/city/WasteBins'));
const WasteCollections = lazy(() => import('../features/city/WasteCollections'));

// Containers
const ContainerBatches     = lazy(() => import('../features/containers/ContainerBatches'));
const ContainerAssignments = lazy(() => import('../features/containers/ContainerAssignments'));

// Notifications
const Notifications      = lazy(() => import('../features/notifications/Notifications'));
const NotificationAlerts = lazy(() => import('../features/notifications/NotificationAlerts'));
const Webhooks           = lazy(() => import('../features/notifications/Webhooks'));

// Billing
const Subscriptions = lazy(() => import('../features/billing/Subscriptions'));
const Invoices      = lazy(() => import('../features/billing/Invoices'));

function PageLoading() {
  return (
    <div className="page-loading">
      <span className="spinner spinner-lg" />
    </div>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, ready } = useAuth();
  if (!ready) return <PageLoading />;
  if (!user)  return <LoginScreen />;
  return <>{children}</>;
}

function RootRedirect() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (!ready) return;
    if (user) navigate('/platform/dashboard', { replace: true });
  }, [ready, user, navigate]);
  if (!ready) return <PageLoading />;
  if (!user)  return <LoginScreen />;
  return <PageLoading />;
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <RequireAuth>
      <AppShell>
        <Suspense fallback={<PageLoading />}>
          {children}
        </Suspense>
      </AppShell>
    </RequireAuth>
  );
}

export function AppRouter() {
  return (
    <HashRouter>
      <Suspense fallback={<PageLoading />}>
        <Routes>
          <Route path="/" element={<RootRedirect />} />

          <Route path="/platform/*" element={
            <Shell>
              <Routes>
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="invites"   element={<Invites />} />
                <Route path="users"     element={<Users />} />
                <Route path="tenants"   element={<Tenants />} />
                <Route path="pending"   element={<Pending />} />
                <Route path="audit"     element={<Audit />} />
                <Route path="*"         element={<Navigate to="dashboard" replace />} />
              </Routes>
            </Shell>
          } />

          <Route path="/apps/*" element={
            <Shell>
              <Routes>
                <Route path="registry"      element={<AppRegistry />} />
                <Route path="feature-flags" element={<FeatureFlags />} />
                <Route path="*"             element={<Navigate to="registry" replace />} />
              </Routes>
            </Shell>
          } />

          <Route path="/analytics/*" element={
            <Shell>
              <Routes>
                <Route index         element={<AnalyticsDashboard />} />
                <Route path="reports" element={<Reports />} />
                <Route path="grafana" element={<GrafanaEmbed />} />
              </Routes>
            </Shell>
          } />

          <Route path="/wcr/*" element={
            <Shell>
              <Routes>
                <Route index element={<WcrDashboard />} />
                <Route path="dashboard" element={<WcrDashboard />} />
                <Route path="vehicles" element={<WcrVehicles />} />
                <Route path="containers" element={<WcrContainers />} />
                <Route path="beacons"   element={<WcrBeacons />} />
                <Route path="ref-data"       element={<WcrRefData />} />
                <Route path="organizations" element={<WcrOrganizations />} />
                <Route path="facilities"    element={<WcrFacilities />} />
                <Route path="hma-sync"       element={<WcrHmaSyncLog />} />
                <Route path="audit-log"      element={<WcrAuditLog />} />
                <Route path="vehicle-claims" element={<WcrVehicleClaims />} />
                <Route path="entity-match"   element={<EntityMatch />} />
                <Route path="entity-aliases" element={<EntityAliasGlossary />} />
                <Route path="resolve-job-runs" element={<ResolveJobRuns />} />
                <Route path="efodsa-rbac"    element={<EfodsaRolesPermissions />} />
                <Route path="assets"         element={<TenantAssets />} />
                <Route path="chat"           element={<WcrChat />} />
                <Route path="*"        element={<Navigate to="vehicles" replace />} />
              </Routes>
            </Shell>
          } />

          <Route path="/hauler/*" element={
            <Shell>
              <Routes>
                <Route path="tenants"    element={<HaulerTenants />} />
                <Route path="contracts"  element={<HaulerContracts />} />
                <Route path="facilities" element={<HaulerFacilities />} />
                <Route path="vehicles"   element={<HaulerVehicles />} />
                <Route path="serviced-seds" element={<ServicedSeds />} />
                <Route path="sessions"   element={<HaulerDriverSessions />} />
                <Route path="movements"  element={<HaulerMovementGroups />} />
                <Route path="*"          element={<Navigate to="tenants" replace />} />
              </Routes>
            </Shell>
          } />

          <Route path="/rehoreca/*" element={
            <Shell>
              <Routes>
                <Route path="activation-orders"     element={<ActivationOrders />} />
                <Route path="deactivation-requests" element={<DeactivationRequests />} />
                <Route path="tenants"               element={<RehorecaTenants />} />
                <Route path="submissions"           element={<Submissions />} />
                <Route path="organizations"         element={<Organizations />} />
                <Route path="business-groups"       element={<BusinessGroups />} />
                <Route path="facilities"            element={<RehorecaFacilities />} />
                <Route path="guest-nights"          element={<GuestNights />} />
                <Route path="pylon-mapping"         element={<PylonMapping />} />
                <Route path="pylon-clients"         element={<PylonClients />} />
                <Route path="pylon-items"           element={<PylonItems />} />
                <Route path="pylon-log"             element={<PylonLog />} />
                <Route path="label-batches"         element={<LabelBatches />} />
                <Route path="certificate-diagnostics" element={<CertificateDiagnostics />} />
                <Route path="*"                     element={<Navigate to="activation-orders" replace />} />
              </Routes>
            </Shell>
          } />

          <Route path="/city/*" element={
            <Shell>
              <Routes>
                <Route path="municipalities" element={<Municipalities />} />
                <Route path="producers"      element={<Producers />} />
                <Route path="bins"           element={<WasteBins />} />
                <Route path="collections"    element={<WasteCollections />} />
                <Route path="*"              element={<Navigate to="municipalities" replace />} />
              </Routes>
            </Shell>
          } />

          <Route path="/containers/*" element={
            <Shell>
              <Routes>
                <Route path="batches"     element={<ContainerBatches />} />
                <Route path="assignments" element={<ContainerAssignments />} />
                <Route path="*"           element={<Navigate to="batches" replace />} />
              </Routes>
            </Shell>
          } />

          <Route path="/notifications/*" element={
            <Shell>
              <Routes>
                <Route index           element={<Notifications />} />
                <Route path="alerts"   element={<NotificationAlerts />} />
                <Route path="webhooks" element={<Webhooks />} />
              </Routes>
            </Shell>
          } />

          <Route path="/billing/*" element={
            <Shell>
              <Routes>
                <Route index           element={<Subscriptions />} />
                <Route path="invoices" element={<Invoices />} />
              </Routes>
            </Shell>
          } />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </HashRouter>
  );
}
